import { useState, useEffect } from 'react';

/**
 * Hook for notification subscription status tracking
 *
 * Note: Actual notification subscriptions are handled by the global RealtimeQueryProvider.
 * This hook only provides subscription status information for components that need it.
 *
 * @param userId - The user ID to track notifications for
 * @param options - Options (kept for API compatibility but not used for subscriptions)
 */
export function useNotificationSubscription(
  userId: string | null,
  _options: {
    /** Whether to enable debounced invalidation (default: true) */
    enableDebouncing?: boolean;
    /** Debounce delay in milliseconds (default: 300) */
    debounceDelay?: number;
  } = {}
) {
  // Track subscription health for error boundary integration
  const [subscriptionHealth, setSubscriptionHealth] = useState<{
    isHealthy: boolean;
    lastError?: string;
    lastSuccessfulConnection?: Date;
  }>({
    isHealthy: true,
  });

  // Update health status when user changes
  useEffect(() => {
    if (userId) {
      setSubscriptionHealth(prev => ({
        ...prev,
        isHealthy: true,
        lastSuccessfulConnection: new Date(),
      }));
    }
  }, [userId]);

  // Return subscription status for debugging and error boundaries
  return {
    isActive: !!userId,
    userId,
    health: subscriptionHealth,
    // Method to manually trigger error state (for testing)
    _triggerError: (error: string) => {
      setSubscriptionHealth(prev => ({
        ...prev,
        isHealthy: false,
        lastError: error,
      }));
    },
  };
}
